function checkpointAction(key, selectedRangePath) {
  const actions = {
    profile: `${selectedRangePath}/settings#settings-profile`,
    branding: `${selectedRangePath}/settings`,
    business_hours: `${selectedRangePath}/settings#settings-hours`,
    membership_app: `${selectedRangePath}/applications`,
    resources_app: `${selectedRangePath}/applications`,
    membership_config: `${selectedRangePath}/apps/membership`,
    public_plans: `${selectedRangePath}/apps/membership`,
    payments: `${selectedRangePath}/payments`,
    resources: `${selectedRangePath}/apps/resources`
  };
  return actions[key] || selectedRangePath;
}

function checkpointLabel(item) {
  const labels = {
    tenant_lifecycle: "Range workspace is active",
    profile: "Public range profile",
    branding: "Member-facing brand details",
    business_hours: "Operating schedule",
    membership_app: "Membership app",
    resources_app: "Facilities app",
    membership_config: "Membership setup",
    public_plans: "Public membership plans",
    payments: "Payments",
    resources: "Bookable facilities"
  };
  return labels[item.key] || item.label || "Setup item";
}

function checkpointDetail(item) {
  if (item.key === "tenant_lifecycle") {
    return item.complete ? "The owner workspace is active." : "Finish workspace activation before publishing public flows.";
  }
  return item.detail || "Open this setup area and finish the missing information.";
}

function launchSetupItems(selectedRangePath, checkpointByKey = new Map()) {
  return [
    {
      key: "profile",
      label: "Public profile",
      detail: "Name, contact, location, and member-facing description.",
      path: `${selectedRangePath}/settings#settings-profile`,
      complete: checkpointByKey.get("profile")?.complete
    },
    {
      key: "business_hours",
      label: "Operating hours",
      detail: "Set the schedule members should trust before booking.",
      path: `${selectedRangePath}/settings#settings-hours`,
      complete: checkpointByKey.get("business_hours")?.complete
    },
    {
      key: "resources",
      label: "Resources and reservations",
      detail: "Add lanes, bays, blocks, duration caps, and reservation rules.",
      path: `${selectedRangePath}/apps/resources`,
      complete: checkpointByKey.get("resources")?.complete
    },
    {
      key: "membership_config",
      label: "Member application",
      detail: "Collect the screening information the owner needs for approval.",
      path: `${selectedRangePath}/apps/membership#membership-application`,
      complete: checkpointByKey.get("membership_config")?.complete
    },
    {
      key: "public_plans",
      label: "Membership plans",
      detail: "Publish the plans that should appear in the member flow.",
      path: `${selectedRangePath}/apps/membership#membership-plans`,
      complete: checkpointByKey.get("public_plans")?.complete
    },
    {
      key: "payments",
      label: "Range payments",
      detail: "Connect Stripe so member and event payments can be collected.",
      path: `${selectedRangePath}/payments`,
      complete: checkpointByKey.get("payments")?.complete
    },
    {
      key: "events",
      label: "First class or event",
      detail: "Create one program so members can see the class experience.",
      path: `${selectedRangePath}/apps/events`,
      complete: false
    },
    {
      key: "access",
      label: "Staff access",
      detail: "Invite admins, instructors, staff, or chapter leads.",
      path: `${selectedRangePath}/access`,
      complete: false
    },
    {
      key: "public_domain",
      label: "Public website and domain",
      detail: "Review the public page and connect a custom domain when ready.",
      path: `${selectedRangePath}/settings#settings-domain`,
      complete: false
    },
    {
      key: "platform_billing",
      label: "RangeGrid billing",
      detail: "Trial is active. Finish platform billing before production launch.",
      path: `${selectedRangePath}/apps/launch`,
      complete: false
    }
  ];
}

export function LaunchCommandPage({
  loading,
  navigate,
  readiness,
  selectedRange,
  selectedRangePath
}) {
  const effectiveReadiness = readiness;
  const checkpoints = effectiveReadiness?.checkpoints || [];
  const checkpointByKey = new Map(checkpoints.map((item) => [item.key, item]));
  const setupItems = launchSetupItems(selectedRangePath, checkpointByKey);
  const incomplete = checkpoints.filter((item) => !item.complete);
  const progress = effectiveReadiness?.summary?.total
    ? Math.round((Number(effectiveReadiness.summary.complete || 0) / Number(effectiveReadiness.summary.total || 1)) * 100)
    : 0;

  return (
    <div className="ops-page">
      {loading && <div className="status-strip">Loading launch readiness...</div>}
      <section className="panel launch-hero-panel">
        <div className="panel-header">
          <div>
            <h2>Launch readiness</h2>
            <p>Check the owner setup that must be complete before members can confidently use public signup, reservations, and payments.</p>
          </div>
          <span className={effectiveReadiness?.ready ? "status-badge status-active" : "status-badge status-trial"}>
            {effectiveReadiness?.ready ? "Ready" : `${progress}% ready`}
          </span>
        </div>
        <div className="launch-progress" aria-label="Launch readiness progress">
          <span style={{ width: `${progress}%` }} />
        </div>
      </section>

      <section className="panel readiness-panel">
        <div className="panel-header">
          <div>
            <h2>Owner launch path</h2>
            <p>Work these setup areas before inviting members into the production flow.</p>
          </div>
        </div>
        <div className="launch-setup-list">
          {setupItems.map((item) => (
            <button
              type="button"
              className={item.complete ? "is-complete" : ""}
              key={item.key}
              onClick={() => navigate(item.path)}
            >
              <span>{item.complete ? "Done" : "Next"}</span>
              <strong>{item.label}</strong>
              <small>{item.detail}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="panel readiness-panel">
        <div className="panel-header">
          <div>
            <h2>Launch checklist</h2>
            <p>{selectedRange?.name || "This range"} needs each setup area complete before public member flows are ready.</p>
          </div>
        </div>
        <div className="readiness-list">
          {checkpoints.map((item) => (
            <div className={item.complete ? "is-complete" : item.key === "tenant_lifecycle" ? "is-blocked" : ""} key={item.key}>
              <strong>{checkpointLabel(item)}</strong>
              <span>{checkpointDetail(item)}</span>
              {!item.complete && (
                <button type="button" className="secondary-button" onClick={() => navigate(checkpointAction(item.key, selectedRangePath))}>
                  Open
                </button>
              )}
            </div>
          ))}
          {!loading && checkpoints.length === 0 && <div><strong>No readiness data</strong><span>Open the range dashboard to refresh setup checkpoints.</span></div>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Next setup step</h2>
            <p>Start with the first incomplete item so launch prep stays linear.</p>
          </div>
          {incomplete[0] && (
            <button
              type="button"
              className="launch-next-action"
              onClick={() => navigate(checkpointAction(incomplete[0].key, selectedRangePath))}
            >
              Open {checkpointLabel(incomplete[0])}
            </button>
          )}
        </div>
        {!incomplete[0] && <div className="empty-state">All launch blockers are clear.</div>}
      </section>
    </div>
  );
}
